import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { finalize, map } from 'rxjs';

import { DiagnosticReport } from '../../../../core/interfaces/diagnostic-report';
import { DiagnosticReportService } from '../../../../core/services/diagnostic/diagnostic-report.service';
import { ChartTooltipComponent } from './charts/chart-tooltip.component';
import { ReportChromeService } from './report-chrome.service';
import { ReportI18nService } from './report-i18n.service';
import { ReportFmtPipe, ReportTPipe } from './report-i18n.pipe';
import { ReportPrintService } from './report-print.service';
import { ReportViewBuilder } from './report-view.builder';
import { ReportScope } from './report.model';
import { AllSubjectsSection } from './sections/all-subjects.section';
import { ComparisonSection } from './sections/comparison.section';
import { ReportFooterSection } from './sections/report-footer.section';
import { ReportHeaderComponent } from './sections/report-header.component';
import { ReportPrintCoverComponent } from './sections/report-print-cover.component';
import { SubjectPanelComponent } from './sections/subject-panel.component';

/**
 * Rapport de diagnostic — l'écran, et le PDF.
 *
 * ── Pourquoi ce n'est plus un document autonome ────────────────────────────
 * Le rapport était livré comme une page HTML statique de `src/assets/`, ouverte
 * dans un nouvel onglet, à qui un script passait un jeton par `window.name`
 * puis réécrivait le texte de la maquette par-dessus ses valeurs de
 * démonstration. Le montage tenait tant que la maquette ne bougeait pas : les
 * compteurs étaient retrouvés **par leur valeur de démonstration**, les
 * libellés de l'API voyageaient déguisés en clés de traduction, et les paliers
 * comme le plan de remédiation étaient des pourcentages écrits en dur.
 *
 * Tout est désormais une vue Angular alimentée par l'API. Il n'existe plus
 * qu'un seul rendu — celui-ci — donc plus de second document voué à s'en
 * écarter, et plus rien de statique dans le rapport.
 *
 * ── Deux portées, un seul composant ────────────────────────────────────────
 * `?subject=<id>` donne le rapport **d'une matière** ; sans lui, le rapport
 * **complet** : le même document répété pour chaque matière, suivi des deux
 * seuls blocs d'ensemble — « toutes les matières » et « comparaison des
 * performances ». Les deux s'impriment, et le PDF du rapport complet contient
 * toutes les matières, chacune sur ses pages.
 *
 * ── Pourquoi la page est sans habillage ────────────────────────────────────
 * La route est déclarée hors des coquilles de rôle : ni barre latérale, ni
 * en-tête applicatif. Le rapport porte les siens, sa feuille A4 suppose la
 * largeur entière, et l'export PDF est l'impression de cette page — imprimer
 * la coquille avec reviendrait à livrer un document encadré par un menu.
 */
@Component({
  selector: 'app-diagnostic-report',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    ReportTPipe,
    ReportFmtPipe,
    ChartTooltipComponent,
    ReportPrintCoverComponent,
    ReportHeaderComponent,
    SubjectPanelComponent,
    AllSubjectsSection,
    ComparisonSection,
    ReportFooterSection,
  ],
  templateUrl: './diagnostic-report.component.html',
  styleUrls: ['./report.theme.css', './report-page.css'],
  providers: [ReportChromeService, ReportPrintService],
})
export class DiagnosticReportComponent {
  private readonly reports = inject(DiagnosticReportService);
  private readonly builder = inject(ReportViewBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly i18n = inject(ReportI18nService);

  protected readonly chrome = inject(ReportChromeService);

  /** Élève affiché, ou `null` pour l'élève connecté. */
  private readonly studentId = this.readStudentId();

  protected readonly report = signal<DiagnosticReport | null>(null);
  protected readonly loading = signal(true);
  protected readonly errored = signal(false);

  /**
   * Matière demandée par l'adresse.
   *
   * Elle décide de la portée : une matière, ou toutes. Elle est lue en continu
   * plutôt qu'une fois, pour qu'un lien vers une autre matière reste un simple
   * changement d'adresse.
   */
  private readonly requestedSubjectId = toSignal(
    this.route.queryParamMap.pipe(map((params) => toId(params.get('subject')))),
    { initialValue: toId(this.route.snapshot.queryParamMap.get('subject')) },
  );

  protected readonly scope = computed<ReportScope>(() =>
    this.requestedSubjectId() === null ? 'all' : 'subject',
  );

  /**
   * Panneau affiché à l'écran en portée complète.
   *
   * `null` tant que l'utilisateur n'a rien choisi : la vue ouvre alors la
   * première matière. Ce choix ne touche pas à l'adresse — c'est de la
   * consultation, pas une autre page — et il n'a aucun effet sur le tirage,
   * qui contient toutes les matières.
   */
  private readonly pickedPanelId = signal<number | null>(null);

  protected readonly view = computed(() => {
    const report = this.report();
    if (!report) return null;

    return this.builder.build(
      report,
      this.scope(),
      this.requestedSubjectId(),
      this.chrome.theme(),
    );
  });

  /** Matière dont le panneau est visible. */
  protected readonly activePanelId = computed<number | null>(() => {
    const view = this.view();
    if (!view) return null;

    const picked = this.pickedPanelId();
    const exists = view.subjects.some((s) => s.subjectId === picked);

    return exists ? picked : (view.subjects[0]?.subjectId ?? null);
  });

  /** Vrai quand l'élève n'a aucune matière affectée : il n'y a rien à montrer. */
  protected readonly empty = computed(() => (this.view()?.subjects.length ?? 0) === 0);

  constructor() {
    /* Les libellés — matières, compétences, paliers, niveaux — sont traduits
       par le serveur à partir du paramètre `language`. Changer de langue en
       cours de lecture impose donc de redemander le rapport : traduire côté
       client ne ferait que réécrire l'habillage autour de libellés restés dans
       la langue précédente. */
    effect(
      () => {
        const lang = this.i18n.lang();
        this.load(lang);
      },
      { allowSignalWrites: true },
    );
  }

  private load(language: string): void {
    this.loading.set(true);
    this.errored.set(false);

    const request$ = this.studentId
      ? this.reports.getForStudent(this.studentId, language)
      : this.reports.getMine(language);

    request$.pipe(finalize(() => this.loading.set(false))).subscribe({
      next: (report) => this.report.set(report),
      error: () => {
        // Aucun rapport de démonstration en repli : une erreur reste une erreur.
        this.report.set(null);
        this.errored.set(true);
      },
    });
  }

  /* ── Navigation ──────────────────────────────────────────────── */

  /**
   * Choix dans la barre de matières.
   *
   * `null` désigne l'entrée « toutes les matières », qui ne change pas de
   * panneau mais amène aux deux blocs d'ensemble, en bas du document.
   */
  protected onSubjectSelected(subjectId: number | null): void {
    if (subjectId === null) {
      document.getElementById('subjects')?.scrollIntoView({ block: 'start' });
      return;
    }

    this.pickedPanelId.set(subjectId);
    document.getElementById('report-content')?.scrollIntoView({ block: 'start' });
  }

  /**
   * Retour.
   *
   * `history.back()` plutôt qu'une route fixe : on arrive ici depuis l'écran de
   * résultat, depuis la liste des matières ou depuis la fiche d'un élève, et
   * renvoyer les trois au même endroit ferait perdre son fil à deux d'entre
   * eux. Sans historique — le rapport ouvert directement par son adresse — on
   * retombe sur le tableau de bord.
   */
  protected goBack(): void {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    void this.router.navigate([this.studentId ? '/admin/students' : '/student/dashboard']);
  }

  private readStudentId(): number | null {
    return toId(this.route.snapshot.paramMap.get('studentId'));
  }
}

/** Identifiant d'adresse : positif, ou rien. Un `0` ne désigne aucune ligne. */
function toId(raw: string | null): number | null {
  const value = Number(raw);
  return raw !== null && Number.isFinite(value) && value > 0 ? value : null;
}
